import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { FiMail, FiLock, FiLogIn, FiEye, FiEyeOff } from 'react-icons/fi';

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post('/api/admin/login', form);
      login(data.token, data.email);
      toast.success('Welcome back!');
      navigate('/admin');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', fontFamily: "'Poppins', sans-serif" }}>

      {/* Left — Branding Panel */}
      <div style={{ flex: 1, background: 'linear-gradient(160deg, #0a1f17 0%, #0d2a22 50%, #0f3320 100%)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '60px 48px', position: 'relative', overflow: 'hidden' }}>
        {/* Decorative circles */}
        <div style={{ position: 'absolute', width: 400, height: 400, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.04)', top: -80, right: -80 }} />
        <div style={{ position: 'absolute', width: 280, height: 280, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.04)', bottom: -40, left: -40 }} />
        <div style={{ position: 'absolute', width: 180, height: 180, borderRadius: '50%', background: 'rgba(26,107,90,0.12)', top: '30%', right: '10%' }} />

        <div style={{ position: 'relative', textAlign: 'center', maxWidth: 360 }}>
          <div style={{ width: 72, height: 72, borderRadius: 20, background: 'linear-gradient(135deg,#1a6b5a,#e07b39)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', margin: '0 auto 28px', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>🏔️</div>
          <h1 style={{ color: '#fff', fontFamily: "'Playfair Display', serif", fontSize: '2rem', fontWeight: 800, lineHeight: 1.2, marginBottom: 16 }}>
            Kashmir Magic Tour
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.95rem', lineHeight: 1.75, marginBottom: 40 }}>
            Manage your tours, bookings, enquiries and packages from one central admin panel.
          </p>

          {/* Feature list */}
          {['Booking management & approvals', 'Contact & enquiry tracking', 'Tour package control', 'Real-time dashboard'].map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, textAlign: 'left' }}>
              <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'rgba(26,107,90,0.4)', border: '1px solid rgba(26,107,90,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ color: '#6ee7b7', fontSize: '0.6rem', fontWeight: 700 }}>✓</span>
              </div>
              <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>{f}</span>
            </div>
          ))}
        </div>

        {/* Bottom tag */}
        <div style={{ position: 'absolute', bottom: 28, color: 'rgba(255,255,255,0.2)', fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          Secure Admin Access
        </div>
      </div>

      {/* Right — Login Form */}
      <div style={{ width: 480, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', padding: '48px 40px' }}>
        <div style={{ width: '100%', maxWidth: 380 }}>
          <div style={{ marginBottom: 36 }}>
            <h2 style={{ fontSize: '1.6rem', fontWeight: 800, color: '#111827', marginBottom: 6 }}>Sign in</h2>
            <p style={{ color: '#6b7280', fontSize: '0.88rem' }}>Enter your admin credentials to continue</p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Email */}
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                Email address
              </label>
              <div style={{ position: 'relative' }}>
                <FiMail size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                <input
                  type="email" required
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="admin@kashmirmagictour.com"
                  style={{ width: '100%', padding: '11px 14px 11px 42px', border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: '0.88rem', color: '#111827', background: '#fff', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.18s' }}
                  onFocus={e => e.target.style.borderColor = '#1a6b5a'}
                  onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>
            </div>

            {/* Password */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <FiLock size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                <input
                  type={showPwd ? 'text' : 'password'} required
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  style={{ width: '100%', padding: '11px 42px 11px 42px', border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: '0.88rem', color: '#111827', background: '#fff', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.18s' }}
                  onFocus={e => e.target.style.borderColor = '#1a6b5a'}
                  onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                />
                <button type="button" onClick={() => setShowPwd(p => !p)} style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, display: 'flex' }}>
                  {showPwd ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{ width: '100%', padding: '13px', background: loading ? '#9ca3af' : 'linear-gradient(135deg,#1a6b5a,#0d2a22)', color: '#fff', border: 'none', borderRadius: 10, fontSize: '0.92rem', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: loading ? 'none' : '0 4px 16px rgba(13,42,34,0.35)', transition: 'all 0.18s' }}>
              {loading
                ? <><span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} /> Signing in...</>
                : <><FiLogIn size={16} /> Sign In</>
              }
            </button>
          </form>

          <div style={{ marginTop: 28, padding: '14px 16px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: '1rem' }}>🔒</span>
            <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>Authorised personnel only. Unauthorised access is prohibited.</span>
          </div>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
